import React, { useEffect, useState } from 'react';
import './intro.scss';

// ---------------------------------------------------------------------------
// Opening sequence — geometry, not typography (the wordmark belongs to the
// hero alone).
//
//   1. build — five skewed parallelogram bars rise through masks at centre
//              and settle into a slow breathing loop; beneath them:
//              "Loading — your experience".
//   2. sweep — the moment today's photograph is ready, it assembles across
//              the screen as three parallelogram panels sweeping left to
//              right, while the bars exit upward in stagger.
//   3. fade  — the overlay crossfades away; the hero beneath holds the SAME
//              photograph, and its wordmark rises for the first time.
//
// The parent gates reduced-motion (gentle crossfade variant via `reduced`).
// ---------------------------------------------------------------------------

const safeUrl = (url) => (url ? url.replace(/ /g, '%20') : '');
const introSrc = (image) => safeUrl(image?.file_src || image?.urls?.original);

const BARS = [0, 1, 2, 3, 4];
const PANELS = [0, 1, 2];

const Intro = ({ image, reduced = false, onReveal, onDone }) => {
  const [phase, setPhase] = useState('build'); // build | wait | sweep | fade
  const [imgReady, setImgReady] = useState(false);

  // Preload the hero frame as soon as the data arrives (usually mid-build).
  useEffect(() => {
    if (!image) return undefined;
    const img = new Image();
    img.onload = () => setImgReady(true);
    img.src = introSrc(image);
    return () => { img.onload = null; };
  }, [image]);

  // Hold the loader for at least 1.3s, then sweep as soon as the photograph
  // is ready — or exit gracefully without it by the 3.2s deadline.
  useEffect(() => {
    const hold = setTimeout(() => {
      setPhase((p) => (p === 'build' ? 'wait' : p));
    }, 1300);
    const deadline = setTimeout(() => {
      setPhase((p) => (p === 'build' || p === 'wait' ? 'fade' : p));
    }, 3200);
    return () => { clearTimeout(hold); clearTimeout(deadline); };
  }, []);

  useEffect(() => {
    if (phase === 'wait' && imgReady) setPhase('sweep');
  }, [phase, imgReady]);

  useEffect(() => {
    let t;
    if (phase === 'sweep') t = setTimeout(() => setPhase('fade'), 1200);
    else if (phase === 'fade') {
      onReveal();
      t = setTimeout(onDone, 600);
    }
    return () => clearTimeout(t);
  }, [phase, onReveal, onDone]);

  return (
    <div
      className={`kki kki--${phase} ${reduced ? 'kki--rm' : ''}`}
      aria-hidden="true"
    >
      {/* the photograph, assembling from three parallelogram panels */}
      {image && (
        <div className="kki__panels">
          {PANELS.map((i) => (
            <div key={i} className={`kki__panel kki__panel--${i}`}>
              <img src={introSrc(image)} alt="" draggable={false} />
            </div>
          ))}
        </div>
      )}

      {/* the loader — breathing parallelograms */}
      <div className="kki__stage">
        <div className="kki__bars">
          {BARS.map((i) => (
            <span key={i} className={`kki__barMask kki__barMask--${i}`}>
              <span className={`kki__bar kki__bar--${i}`} />
            </span>
          ))}
        </div>
        <p className="kki__text">
          <span className="kki__caps">Loading</span>
          <em className="kki__ital">your experience</em>
        </p>
      </div>
    </div>
  );
};

export default Intro;
